import { useCallback, useEffect, useLayoutEffect, useRef } from "react";
import { cancelDefer, defer } from "./utils/defer.js";
import { getInputSelection, isInputFocused, setInputSelection } from "./utils/input.js";
import { isDOMElement } from "./utils/helpers.js";
function useInputElement(inputRef) {
    return useCallback(()=>{
        let input = inputRef.current;
        const isDOMNode = "u" > typeof window && null !== input && isDOMElement(input);
        if (!input || !isDOMNode) return null;
        if ('INPUT' !== input.nodeName) input = input.querySelector('input');
        if (!input) throw new Error("react-input-mask: inputComponent doesn't contain input node");
        return input;
    }, [
        inputRef
    ]);
}
function useDeferLoop(callback) {
    const deferIdRef = useRef(null);
    const runLoop = useCallback(()=>{
        if (null !== deferIdRef.current) return;
        function loop() {
            callback();
            deferIdRef.current = defer(loop);
        }
        loop();
    }, [
        callback
    ]);
    const stopLoop = useCallback(()=>{
        if (null !== deferIdRef.current) {
            cancelDefer(deferIdRef.current);
            deferIdRef.current = null;
        }
    }, []);
    useEffect(()=>{
        if (deferIdRef.current) {
            stopLoop();
            runLoop();
        }
    }, [
        runLoop,
        stopLoop
    ]);
    return [
        runLoop,
        stopLoop
    ];
}
function useSelection(inputRef, isMasked) {
    const selectionRef = useRef({
        start: null,
        end: null
    });
    const getInputElement = useInputElement(inputRef);
    const getSelection = useCallback(()=>{
        const input = getInputElement();
        return getInputSelection(input);
    }, [
        getInputElement
    ]);
    const getLastSelection = useCallback(()=>selectionRef.current, []);
    const setSelection = useCallback((selection)=>{
        var _selection_start, _selection_end;
        const input = getInputElement();
        if (!input || !isInputFocused(input)) return;
        setInputSelection(input, null != (_selection_start = selection.start) ? _selection_start : 0, null != (_selection_end = selection.end) ? _selection_end : 0);
        selectionRef.current = getSelection();
    }, [
        getInputElement,
        getSelection
    ]);
    const selectionLoop = useCallback(()=>{
        selectionRef.current = getSelection();
    }, [
        getSelection
    ]);
    const [runSelectionLoop, stopSelectionLoop] = useDeferLoop(selectionLoop);
    useLayoutEffect(()=>{
        if (!isMasked) return;
        const input = getInputElement();
        if (!input) return;
        input.addEventListener('focus', runSelectionLoop);
        input.addEventListener('blur', stopSelectionLoop);
        if (isInputFocused(input)) runSelectionLoop();
        return ()=>{
            input.removeEventListener('focus', runSelectionLoop);
            input.removeEventListener('blur', stopSelectionLoop);
            stopSelectionLoop();
        };
    });
    return {
        getSelection,
        getLastSelection,
        setSelection
    };
}
function useValue(inputRef, initialValue) {
    const getInputElement = useInputElement(inputRef);
    const valueRef = useRef(initialValue);
    const getValue = useCallback(()=>{
        const input = getInputElement();
        return (null == input ? void 0 : input.value) || '';
    }, [
        getInputElement
    ]);
    const getLastValue = useCallback(()=>valueRef.current, []);
    const setValue = useCallback((newValue)=>{
        valueRef.current = newValue;
        const input = getInputElement();
        if (input) input.value = newValue;
    }, [
        getInputElement
    ]);
    return {
        getValue,
        getLastValue,
        setValue
    };
}
function useInputState(initialValue, isMasked) {
    const inputRef = useRef(null);
    const { getSelection, getLastSelection, setSelection } = useSelection(inputRef, isMasked);
    const { getValue, getLastValue, setValue } = useValue(inputRef, initialValue);
    function getLastInputState() {
        return {
            value: getLastValue(),
            selection: getLastSelection()
        };
    }
    function getInputState() {
        return {
            value: getValue(),
            selection: getSelection()
        };
    }
    function setInputState({ value, selection }) {
        setValue(value);
        setSelection(selection);
    }
    return {
        inputRef,
        getInputState,
        getLastInputState,
        setInputState
    };
}
function usePrevious(value) {
    const ref = useRef(void 0);
    useEffect(()=>{
        ref.current = value;
    });
    return ref.current;
}
export { useInputElement, useInputState, usePrevious };
